import i18n from '../i18n';

/**
 * Formats a file size in bytes to a human-readable string (KB or MB).
 * Uses i18n for translations.
 * 
 * Rules:
 * - 1024 bytes = 1 KB
 * - 1024 KB = 1 MB
 * - Under 1024 KB -> shows in KB with 1 decimal
 * - Over 1024 KB -> shows in MB with 1 decimal
 * - Under 1 KB (but > 0) -> shows as "<1 KB" or "0.1 KB" (using 0.1 KB for simplicity)
 * - Invalid/Corrupted -> returns "Unknown" translation
 * 
 * @param bytes Size in bytes
 * @returns Formatted size string
 */
export const getBase64Size = (base64?: string): number => {
    if (!base64 || !base64.startsWith('data:image')) return 0;
    const base64str = base64.split(',')[1];
    if (!base64str) return 0;
    const padding = (base64str.match(/=/g) || []).length;
    return (base64str.length * 3) / 4 - padding;
};

export const formatFileSize = (bytes?: number | null): string => {
    if (bytes === undefined || bytes === null || isNaN(bytes) || bytes < 0) {
        return i18n.t('mannequin.aiGeneration.hangerCard.sizeUnknown');
    }

    if (bytes === 0) {
        return i18n.t('mannequin.aiGeneration.hangerCard.sizeKB', { value: '0' });
    }

    const kb = bytes / 1024;
    
    if (kb < 1) {
        // For very small files, show 0.1 KB as requested in edge cases
        return i18n.t('mannequin.aiGeneration.hangerCard.sizeKB', { value: '0.1' });
    }

    if (kb < 1024) {
        // Show in KB with 1 decimal
        const formattedKb = (Math.round(kb * 10) / 10).toString();
        return i18n.t('mannequin.aiGeneration.hangerCard.sizeKB', { value: formattedKb });
    }

    // Show in MB with 1 decimal
    const mb = kb / 1024;
    const formattedMb = (Math.round(mb * 10) / 10).toString();
    return i18n.t('mannequin.aiGeneration.hangerCard.sizeMB', { value: formattedMb });
};
